/**
 * Confirmation detail — the thread, the timeline, and the available actions.
 *
 * Which actions appear is decided by the state machine and the caller's
 * permissions, both read from the shared contract. The server re-checks
 * everything, so an action rendered here is a suggestion; an action missing
 * here is only a convenience, never a control.
 *
 * Opening the page marks it read, which is what advances RECEIVED to READ for
 * a recipient — Spec §6's read timestamp is generated by this, not by a
 * separate button nobody would press.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  CONFIRMATION_STATUS_LABELS,
  formatPlateNumber,
  type ConfirmationStatus,
  type Priority,
} from '@lto/contracts';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import {
  Button,
  Empty,
  ErrorState,
  Loading,
  Panel,
  PriorityTag,
  StatusBadge,
  formatTimestamp,
} from '../components/ui';
import { AttachmentList, FilePicker, type SentAttachment } from '../components/Attachments';

interface OfficeContact {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly email: string | null;
  readonly contactNo: string | null;
  readonly address: string | null;
}

interface Recipient {
  readonly officeId: string;
  readonly assignedUserId: string | null;
  readonly status: ConfirmationStatus;
  readonly responseDueAt: string | null;
  readonly officeName: string;
  readonly officeCode: string;
  readonly officeEmail: string | null;
  readonly officeContactNo: string | null;
  readonly officeAddress: string | null;
}

interface Detail {
  readonly id: string;
  readonly referenceNo: string | null;
  readonly subject: string;
  readonly plateNumber: string | null;
  readonly status: ConfirmationStatus;
  readonly priority: Priority;
  readonly originOfficeId: string;
  readonly awaitingParty: string;
  readonly createdAt: string;
  readonly sentAt: string | null;
  readonly receivedAt: string | null;
  readonly readAt: string | null;
  readonly firstRepliedAt: string | null;
  readonly resolvedAt: string | null;
  readonly closedAt: string | null;
  readonly recipients: readonly Recipient[];
  readonly originOffice: OfficeContact | null;
}

interface Message {
  readonly id: string;
  readonly messageType: string;
  readonly visibility: string;
  readonly body: string;
  readonly sentAt: string | null;
  readonly senderOfficeId: string;
  readonly senderFirstName: string;
  readonly senderLastName: string;
  readonly senderOfficeName: string;
  readonly attachments: readonly SentAttachment[];
}

/** A reply with files, written as a draft while they upload — see `createDraftReply`. */
interface ReplyInProgress {
  readonly messageId: string;
  readonly internal: boolean;
  readonly uploaded: Map<File, string>;
}

interface TimelineEntry {
  readonly occurredAt: string;
  readonly action: string;
  readonly summary: string;
  readonly actorDisplayName: string | null;
}

/**
 * Spec §6's ladder, rendered from the six authoritative timestamps.
 *
 * Labels come from the shared contract so the ladder and the status badge
 * never disagree about what a state is called.
 */
const STAGES = [
  { key: 'createdAt', label: 'Created' },
  { key: 'sentAt', label: CONFIRMATION_STATUS_LABELS.SENT },
  { key: 'receivedAt', label: CONFIRMATION_STATUS_LABELS.RECEIVED },
  { key: 'readAt', label: CONFIRMATION_STATUS_LABELS.READ },
  { key: 'firstRepliedAt', label: CONFIRMATION_STATUS_LABELS.REPLIED },
  { key: 'resolvedAt', label: CONFIRMATION_STATUS_LABELS.RESOLVED },
  { key: 'closedAt', label: CONFIRMATION_STATUS_LABELS.CLOSED },
] as const;

/**
 * An office and how to reach it.
 *
 * A missing field says so rather than rendering an empty line — "Not recorded"
 * tells the reader the system does not hold the number, where a blank leaves
 * them wondering whether it failed to load.
 */
function OfficeCard({
  heading,
  name,
  code,
  email,
  contactNo,
  address,
  isYou,
  due,
}: {
  heading: string;
  name: string;
  code: string | null;
  email: string | null;
  contactNo: string | null;
  address: string | null;
  isYou: boolean;
  due?: string | null;
}) {
  const missing = <span style={{ color: 'var(--color-ink-subtle)' }}>Not recorded</span>;

  return (
    <Panel>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="text-xs font-medium uppercase tracking-wider"
            style={{ color: 'var(--color-ink-subtle)' }}
          >
            {heading}
          </span>
          {isYou && (
            <span
              className="text-xs font-medium"
              style={{ color: 'var(--color-accent)' }}
            >
              Your office
            </span>
          )}
        </div>

        <div className="mt-1 text-md font-semibold">{name}</div>
        {code !== null && (
          <div className="reference text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
            {code}
          </div>
        )}

        <dl className="mt-3 space-y-1.5 text-sm">
          <div className="flex gap-2">
            <dt className="w-16 shrink-0" style={{ color: 'var(--color-ink-subtle)' }}>Email</dt>
            <dd>
              {email === null ? (
                missing
              ) : (
                <a
                  href={`mailto:${email}`}
                  className="underline-offset-2 hover:underline"
                  style={{ color: 'var(--color-accent)' }}
                >
                  {email}
                </a>
              )}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-16 shrink-0" style={{ color: 'var(--color-ink-subtle)' }}>Phone</dt>
            <dd className="tabular">
              {contactNo === null ? (
                missing
              ) : (
                <a
                  href={`tel:${contactNo.replace(/[^0-9+]/g, '')}`}
                  className="underline-offset-2 hover:underline"
                  style={{ color: 'var(--color-accent)' }}
                >
                  {contactNo}
                </a>
              )}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-16 shrink-0" style={{ color: 'var(--color-ink-subtle)' }}>Address</dt>
            <dd style={{ color: 'var(--color-ink-muted)' }}>{address ?? missing}</dd>
          </div>
          {due !== undefined && due !== null && (
            <div className="flex gap-2">
              <dt className="w-16 shrink-0" style={{ color: 'var(--color-ink-subtle)' }}>Due</dt>
              <dd className="tabular">{formatTimestamp(due)}</dd>
            </div>
          )}
        </dl>
      </div>
    </Panel>
  );
}

export function ConfirmationDetail() {
  const { id = '' } = useParams();
  const { user, can } = useSession();
  const queryClient = useQueryClient();

  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replyProgress, setReplyProgress] = useState<string | null>(null);
  const replyDraft = useRef<ReplyInProgress | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * Live progression — RT-001.
   *
   * The sender opens this page to watch a confirmation move from sent, to
   * received by the office, to read, to replied. Polling every twenty seconds
   * is what makes that watchable without a page refresh.
   *
   * It stops once the confirmation is closed or cancelled: nothing further can
   * happen to it, so continuing to ask would be a request per twenty seconds
   * that can only ever return the same answer. RT-004 holds either way — every
   * timestamp shown here is the server's, and the page is correct the moment
   * it loads whether or not another poll ever runs.
   */
  const livePolling = (data: Detail | undefined): number | false =>
    data === undefined || data.status === 'CLOSED' || data.status === 'CANCELLED'
      ? false
      : 20_000;

  const detail = useQuery({
    queryKey: ['confirmation', id],
    queryFn: () => api.get<Detail>(`/api/v1/confirmations/${id}`),
    refetchInterval: (query) => livePolling(query.state.data),
  });

  const live = livePolling(detail.data);

  const messages = useQuery({
    queryKey: ['confirmation', id, 'messages'],
    queryFn: () => api.get<Message[]>(`/api/v1/confirmations/${id}/messages`),
    refetchInterval: live,
  });

  const timeline = useQuery({
    queryKey: ['confirmation', id, 'timeline'],
    queryFn: () => api.get<TimelineEntry[]>(`/api/v1/confirmations/${id}/timeline`),
    refetchInterval: live,
  });

  /**
   * Mark read on open.
   *
   * Fire-and-forget: a failure here must not block the page. The user came to
   * read the confirmation, and refusing to show it because a bookkeeping call
   * failed would be the wrong trade.
   */
  useEffect(() => {
    if (id === '') return;
    void api
      .post(`/api/v1/confirmations/${id}/read`)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['confirmation', id] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      })
      .catch(() => {
        /* not fatal */
      });
  }, [id, queryClient]);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['confirmation', id] });
    void queryClient.invalidateQueries({ queryKey: ['confirmations'] });
  }

  function onActionError(caught: unknown) {
    setActionError(
      caught instanceof ApiError
        ? (caught.problem?.detail ?? 'That action could not be completed.')
        : 'Could not reach the server.',
    );
  }

  /**
   * A reply without files is one call. With files it is three steps — a draft
   * reply, its files, then sending it — because a sent message never changes,
   * so its files must be on it before it goes. A file refused part-way keeps
   * the draft and what was uploaded, and the next attempt carries on from
   * there.
   */
  const postReply = useMutation({
    mutationFn: async () => {
      if (replyFiles.length === 0 && replyDraft.current === null) {
        await api.post(`/api/v1/confirmations/${id}/messages`, { body: reply, internal });
        return;
      }

      let current = replyDraft.current;
      if (current === null || current.internal !== internal) {
        const created = await api.post<{ messageId: string }>(`/api/v1/confirmations/${id}/messages`, {
          body: reply,
          internal,
          draft: true,
        });
        current = { messageId: created.messageId, internal, uploaded: new Map() };
        replyDraft.current = current;
      }

      for (const [file, attachmentId] of [...current.uploaded]) {
        if (replyFiles.includes(file)) continue;
        await api.del(`/api/v1/attachments/${attachmentId}`);
        current.uploaded.delete(file);
      }

      for (const [index, file] of replyFiles.entries()) {
        if (current.uploaded.has(file)) continue;
        setReplyProgress(`Uploading ${index + 1} of ${replyFiles.length}: ${file.name}`);
        const created = await api.upload<{ id: string }>(
          `/api/v1/confirmations/${id}/attachments?messageId=${current.messageId}`,
          file,
        );
        current.uploaded.set(file, created.id);
      }

      setReplyProgress('Sending');
      await api.post(`/api/v1/confirmations/${id}/messages/${current.messageId}/send`, { body: reply });
      replyDraft.current = null;
    },
    onSuccess: () => {
      setReply('');
      setInternal(false);
      setReplyFiles([]);
      setReplyProgress(null);
      setActionError(null);
      refresh();
    },
    onError: (caught) => {
      setReplyProgress(null);
      onActionError(caught);
    },
  });

  const transition = useMutation({
    mutationFn: (action: 'resolve' | 'close') =>
      api.post(`/api/v1/confirmations/${id}/${action}`),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: onActionError,
  });

  if (detail.isPending) return <Loading label="Loading confirmation" />;

  if (detail.isError) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <Panel>
        {notFound ? (
          <Empty
            title="Confirmation not found"
            detail="It may not exist, or it may belong to another office."
            action={
              <Link to="/confirmations">
                <Button>Back to confirmations</Button>
              </Link>
            }
          />
        ) : (
          <ErrorState
            message="Could not load this confirmation."
            onRetry={() => void detail.refetch()}
          />
        )}
      </Panel>
    );
  }

  const c = detail.data;
  const isOrigin = c.originOfficeId === user?.officeId;
  const terminal = c.status === 'CLOSED' || c.status === 'CANCELLED';

  // Mirrors the server's rules so the UI does not offer an action that would
  // certainly be refused. The server decides regardless.
  const canReply = can('confirmation.reply') && !terminal && c.status !== 'DRAFT';
  const canResolve =
    can('confirmation.resolve') && isOrigin && (c.status === 'READ' || c.status === 'REPLIED');
  const canClose = can('confirmation.close') && isOrigin && c.status === 'RESOLVED';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="reference text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
              {c.referenceNo ?? 'Draft'}
            </span>
            <StatusBadge status={c.status} full />
            <PriorityTag priority={c.priority} />
            {c.plateNumber !== null && (
              <span
                className="reference border px-1.5 py-0.5 text-xs font-medium"
                style={{
                  borderRadius: 'var(--radius-sm)',
                  borderColor: 'var(--color-rule-strong)',
                  color: 'var(--color-ink-muted)',
                }}
                title="Plate number — the owner can follow this confirmation's progress by it"
              >
                Plate {formatPlateNumber(c.plateNumber)}
              </span>
            )}
          </div>
          <h1 className="mt-1 text-lg font-semibold tracking-tight">{c.subject}</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          {canResolve && (
            <Button
              variant="primary"
              loading={transition.isPending}
              onClick={() => transition.mutate('resolve')}
            >
              Mark resolved
            </Button>
          )}
          {canClose && (
            <Button
              variant="primary"
              loading={transition.isPending}
              onClick={() => transition.mutate('close')}
            >
              Close
            </Button>
          )}
          <Link to="/confirmations">
            <Button variant="ghost">Back</Button>
          </Link>
        </div>
      </div>

      {actionError !== null && (
        <div
          role="alert"
          className="animate-rise border px-3 py-2 text-sm"
          style={{
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-danger-subtle)',
            borderColor: 'var(--color-danger)',
            color: 'var(--color-danger)',
          }}
        >
          {actionError}
        </div>
      )}

      {/* Who this is between, and how to reach them.
          Someone chasing an overdue confirmation needs the office's phone
          number here, not two screens away. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <OfficeCard
          heading="From"
          name={c.originOffice?.name ?? 'Unknown office'}
          code={c.originOffice?.code ?? null}
          email={c.originOffice?.email ?? null}
          contactNo={c.originOffice?.contactNo ?? null}
          address={c.originOffice?.address ?? null}
          isYou={c.originOffice?.id === user?.officeId}
        />
        {c.recipients.map((r) => (
          <OfficeCard
            key={r.officeId}
            heading="To"
            name={r.officeName}
            code={r.officeCode}
            email={r.officeEmail}
            contactNo={r.officeContactNo}
            address={r.officeAddress}
            isYou={r.officeId === user?.officeId}
            due={r.responseDueAt}
          />
        ))}
      </div>

      {/* Progress — Spec §6. Each stage shows its authoritative timestamp, or
          nothing at all if it has not happened. */}
      <Panel title="Progress">
        <ol className="flex flex-wrap gap-x-6 gap-y-3 px-4 py-3">
          {STAGES.map((stage) => {
            const at = c[stage.key];
            const done = at !== null && at !== undefined;
            return (
              <li key={stage.key} className="min-w-[7rem]">
                <div className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="size-1.5 rounded-full"
                    style={{
                      background: done ? 'var(--color-status-resolved)' : 'var(--color-rule-strong)',
                    }}
                  />
                  <span
                    className="text-xs font-medium uppercase tracking-wider"
                    style={{ color: done ? 'var(--color-ink)' : 'var(--color-ink-subtle)' }}
                  >
                    {stage.label}
                  </span>
                </div>
                <div
                  className="tabular mt-0.5 pl-3 text-xs"
                  style={{ color: 'var(--color-ink-subtle)' }}
                >
                  {done ? formatTimestamp(at) : '—'}
                </div>
              </li>
            );
          })}
        </ol>
      </Panel>

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          <Panel title="Conversation">
            {messages.isPending && <Loading label="Loading messages" />}
            {messages.isSuccess && messages.data.length === 0 && (
              <Empty title="No messages yet" />
            )}
            {messages.isSuccess && messages.data.length > 0 && (
              <ul className="stagger divide-y" style={{ borderColor: 'var(--color-rule)' }}>
                {messages.data.map((m) => {
                  const mine = m.senderOfficeId === user?.officeId;
                  const note = m.messageType === 'INTERNAL_NOTE';
                  return (
                    <li
                      key={m.id}
                      className="px-4 py-3"
                      style={{
                        // Internal notes are visually distinct because they are
                        // not visible to the other office — showing them
                        // identically would invite writing one by mistake.
                        background: note ? 'var(--color-sunken)' : undefined,
                        borderLeft: note ? '2px solid var(--color-warning)' : undefined,
                      }}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-sm font-medium">
                          {m.senderFirstName} {m.senderLastName}
                          <span className="ml-2 font-normal" style={{ color: 'var(--color-ink-subtle)' }}>
                            {m.senderOfficeName}
                            {mine ? ' · your office' : ''}
                          </span>
                        </span>
                        <span className="tabular text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                          {formatTimestamp(m.sentAt)}
                        </span>
                      </div>
                      {note && (
                        <div
                          className="mt-1 text-xs font-medium uppercase tracking-wider"
                          style={{ color: 'var(--color-warning)' }}
                        >
                          Internal note — not visible to the other office
                        </div>
                      )}
                      <p className="mt-1.5 text-base whitespace-pre-wrap" style={{ color: 'var(--color-ink-muted)' }}>
                        {m.body}
                      </p>
                      <AttachmentList attachments={m.attachments} onError={setActionError} />
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          {canReply && (
            <Panel title="Reply">
              <form
                className="space-y-3 p-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  postReply.mutate();
                }}
              >
                <textarea
                  rows={4}
                  required
                  maxLength={20_000}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a reply…"
                  aria-label="Reply"
                  className="interactive w-full border px-2.5 py-2 text-base"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    borderColor: 'var(--color-rule-strong)',
                    background: 'var(--color-surface)',
                    resize: 'vertical',
                  }}
                />
                {can('attachment.upload') && (
                  <FilePicker files={replyFiles} onChange={setReplyFiles} disabled={postReply.isPending} />
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={internal}
                      onChange={(e) => setInternal(e.target.checked)}
                    />
                    Internal note — only my office can see this
                  </label>
                  <div className="flex items-center gap-2">
                    {replyProgress !== null && (
                      <span className="text-xs" role="status" style={{ color: 'var(--color-ink-subtle)' }}>
                        {replyProgress}…
                      </span>
                    )}
                    <Button
                      type="submit"
                      variant="primary"
                      loading={postReply.isPending}
                      disabled={reply.trim().length === 0}
                    >
                      {internal ? 'Add note' : 'Send reply'}
                    </Button>
                  </div>
                </div>
              </form>
            </Panel>
          )}
        </div>

        {/* Audit timeline — Spec §15. Read from the trail, not reconstructed
            from the record, because only the trail knows who did what. */}
        <Panel title="Activity" description="From the audit trail">
          {timeline.isPending && <Loading label="Loading activity" />}
          {timeline.isSuccess && timeline.data.length === 0 && <Empty title="No activity recorded" />}
          {timeline.isSuccess && timeline.data.length > 0 && (
            <ol className="stagger px-4 py-3">
              {timeline.data.map((entry, index) => (
                <li key={`${entry.occurredAt}-${index}`} className="relative pb-4 pl-4 last:pb-0">
                  <span
                    aria-hidden="true"
                    className="absolute top-1.5 left-0 size-1.5 rounded-full"
                    style={{ background: 'var(--color-rule-strong)' }}
                  />
                  {index < timeline.data.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="absolute top-3 bottom-0 left-[3px] w-px"
                      style={{ background: 'var(--color-rule)' }}
                    />
                  )}
                  <div className="tabular text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                    {formatTimestamp(entry.occurredAt)}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--color-ink-muted)' }}>
                    {entry.summary}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
    </div>
  );
}
